import React, { useState, useEffect } from "react";
import {
  CreditCard,
  Search,
  RefreshCw,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Calendar,
  IndianRupee,
  ShieldCheck,
} from "lucide-react";
import { useMasterAdmin } from "../../context/MasterAdminAuthContext";
import { toast } from "sonner";

export default function MasterAdminMemberships() {
  const { masterApi } = useMasterAdmin();
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchMemberships = async () => {
    try {
      setLoading(true);
      const res = await masterApi.get("/master-admin/memberships", {
        params: { query: searchQuery, status: statusFilter },
      });
      setMemberships(res.data.memberships || []);
    } catch (err) {
      toast.error("Failed to load memberships");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMemberships();
  }, [statusFilter]);

  const handleStatusChange = async (membershipId, newStatus) => {
    try {
      const formData = new FormData();
      formData.append("status", newStatus);
      await masterApi.put(`/master-admin/memberships/${membershipId}/status`, formData);
      toast.success(`Membership status changed to ${newStatus}`);
      fetchMemberships();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  return (
    <div className="flex flex-col gap-6 max-w-7xl mx-auto pb-10">
      {/* Header & Plan Summary */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-black text-white tracking-tight flex items-center gap-2">
            <span>Membership Management</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30 font-bold">
              ₹99 / Month Plan
            </span>
          </h1>
          <p className="text-xs text-zinc-400 mt-0.5">
            Monitor subscriber subscriptions, 7-day grace periods, and lifecycle states.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchMemberships}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-xs font-semibold text-zinc-300 hover:text-white transition-all"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Plan Feature Highlight Card */}
      <div className="bg-gradient-to-r from-amber-500/10 via-amber-400/5 to-transparent border border-amber-500/20 rounded-2xl p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center text-amber-400 font-bold shrink-0">
            <IndianRupee className="w-6 h-6" />
          </div>
          <div>
            <div className="text-base font-bold text-white flex items-center gap-2">
              <span>PrintQ Monthly Subscription</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 font-bold">
                Standard Commercial Plan
              </span>
            </div>
            <p className="text-xs text-zinc-400 mt-0.5">
              Includes unlimited scans, filters, A4 document formatting, fixed CR80 ID Card mode, and Windows printer integration for 1 PC.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <div className="bg-zinc-950/80 border border-zinc-800 rounded-xl px-4 py-2 text-center">
            <div className="text-xs text-zinc-400">Price</div>
            <div className="text-base font-black text-amber-400 font-mono">₹99 <span className="text-[10px] text-zinc-500 font-normal">/ mo</span></div>
          </div>
          <div className="bg-zinc-950/80 border border-zinc-800 rounded-xl px-4 py-2 text-center">
            <div className="text-xs text-zinc-400">Grace Period</div>
            <div className="text-base font-black text-white font-mono">7 Days</div>
          </div>
        </div>
      </div>

      {/* Memberships Table */}
      <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl overflow-hidden shadow-xl">
        {loading ? (
          <div className="flex items-center justify-center h-48 text-zinc-400">
            <div className="w-6 h-6 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : memberships.length === 0 ? (
          <div className="p-10 text-center text-xs text-zinc-500">
            No membership records found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="bg-zinc-950/60 text-zinc-400 border-b border-zinc-800 font-medium">
                  <th className="py-3 px-4">Membership ID</th>
                  <th className="py-3 px-4">User</th>
                  <th className="py-3 px-4">Plan & Fee</th>
                  <th className="py-3 px-4">Start Date</th>
                  <th className="py-3 px-4">Expiry Date</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60">
                {memberships.map((m) => {
                  const isExp = new Date(m.expiry_date) < new Date();
                  return (
                    <tr key={m.membership_id} className="hover:bg-zinc-800/30 transition-colors">
                      <td className="py-3.5 px-4 font-mono text-zinc-300 font-semibold">{m.membership_id}</td>
                      <td className="py-3.5 px-4 font-bold text-white font-mono">@{m.username}</td>
                      <td className="py-3.5 px-4">
                        <div className="font-semibold text-white">{m.plan_name}</div>
                        <div className="text-[10px] text-emerald-400 font-mono">₹{m.price} / monthly</div>
                      </td>
                      <td className="py-3.5 px-4 text-zinc-400 font-mono">
                        {new Date(m.start_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                      </td>
                      <td className="py-3.5 px-4 font-mono">
                        <span className={isExp ? "text-rose-400 font-bold" : "text-amber-400 font-bold"}>
                          {new Date(m.expiry_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                        </span>
                      </td>
                      <td className="py-3.5 px-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                            m.status === "active" && !isExp
                              ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                              : isExp
                              ? "bg-rose-500/10 text-rose-400 border border-rose-500/20"
                              : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full ${isExp ? "bg-rose-400" : "bg-emerald-400"}`} />
                          <span className="capitalize">{isExp ? "Expired" : m.status}</span>
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {m.status === "active" ? (
                            <button
                              onClick={() => handleStatusChange(m.membership_id, "suspended")}
                              className="px-2 py-1 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-[11px]"
                            >
                              Suspend
                            </button>
                          ) : (
                            <button
                              onClick={() => handleStatusChange(m.membership_id, "active")}
                              className="px-2 py-1 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-[11px]"
                            >
                              Activate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
